import { Component } from '@theme/component';

/**
 * Gourmet Pantry carousel (sections/gp-recipes.liquid, Layout: Carousel).
 *
 * The track is a native horizontal scroll container with scroll snapping, so swiping and
 * trackpad scrolling work without this script. The script adds previous/next buttons that
 * move one card at a time (disabled at the ends) and, with data-autoplay, advances every
 * data-interval milliseconds, looping back to the start. Autoplay pauses while the pointer
 * is over the carousel, while it holds focus, while a finger is on it, while it is off
 * screen or the tab is hidden, and stays off when the shopper presses pause or prefers
 * reduced motion.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - Scrolling list of slides.
 * @property {HTMLElement} [controls] - Button group, hidden while every slide fits in view.
 * @property {HTMLButtonElement} [previousButton]
 * @property {HTMLButtonElement} [nextButton]
 * @property {HTMLButtonElement} [pauseButton] - Pause/play toggle, shown only with autoplay.
 * @property {HTMLElement} [pauseIcon]
 * @property {HTMLElement} [playIcon]
 *
 * @extends {Component<Refs>}
 */
class GpCarousel extends Component {
  requiredRefs = ['track'];

  /** @type {AbortController | undefined} */
  #listeners;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {IntersectionObserver | undefined} */
  #visibilityObserver;
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #timer;
  /** @type {number | undefined} */
  #frame;
  /** Reasons autoplay is on hold right now (hover, focus, touch, offscreen, hidden). */
  #holds = new Set();
  #userPaused = false;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  /** Moves one card forward. */
  next() {
    this.#move(1);
    this.#restartTimer();
  }

  /** Moves one card back. */
  previous() {
    this.#move(-1);
    this.#restartTimer();
  }

  /** Pauses or resumes autoplay. */
  toggleAutoplay() {
    this.#userPaused = !this.#userPaused;
    this.#renderPauseButton();
    this.#restartTimer();
  }

  get #autoplay() {
    return this.hasAttribute('data-autoplay') && !matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  get #interval() {
    return Math.max(2000, Number(this.dataset.interval) || 5000);
  }

  #setup() {
    this.#teardown();
    const { track } = this.refs;
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };

    track.addEventListener('scroll', () => this.#queueUpdate(), { ...options, passive: true });
    this.#resizeObserver = new ResizeObserver(() => this.#queueUpdate());
    this.#resizeObserver.observe(track);

    if (this.#autoplay) {
      const hold = (/** @type {string} */ reason, /** @type {boolean} */ on) => {
        if (on) this.#holds.add(reason);
        else this.#holds.delete(reason);
        this.#restartTimer();
      };
      this.addEventListener('pointerenter', (event) => event.pointerType === 'mouse' && hold('hover', true), options);
      this.addEventListener('pointerleave', (event) => event.pointerType === 'mouse' && hold('hover', false), options);
      this.addEventListener('focusin', () => hold('focus', true), options);
      this.addEventListener(
        'focusout',
        (event) => {
          if (!(event.relatedTarget instanceof Node) || !this.contains(event.relatedTarget)) hold('focus', false);
        },
        options
      );
      track.addEventListener('touchstart', () => hold('touch', true), { ...options, passive: true });
      track.addEventListener('touchend', () => hold('touch', false), { ...options, passive: true });
      track.addEventListener('touchcancel', () => hold('touch', false), { ...options, passive: true });
      document.addEventListener('visibilitychange', () => hold('hidden', document.hidden), options);
      // Several entries can arrive in one batch; the last one is the current state.
      this.#visibilityObserver = new IntersectionObserver((entries) => hold('offscreen', !entries.at(-1)?.isIntersecting));
      this.#visibilityObserver.observe(this);
    }

    this.#renderPauseButton();
    this.#update();
    this.#restartTimer();
  }

  #teardown() {
    this.#listeners?.abort();
    this.#resizeObserver?.disconnect();
    this.#visibilityObserver?.disconnect();
    if (this.#timer) clearInterval(this.#timer);
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#timer = undefined;
    this.#frame = undefined;
    this.#holds.clear();
  }

  #restartTimer() {
    if (this.#timer) clearInterval(this.#timer);
    this.#timer = undefined;
    if (!this.#autoplay || this.#userPaused || this.#holds.size > 0 || !this.#scrollable) return;
    this.#timer = setInterval(() => this.#advance(), this.#interval);
  }

  #advance() {
    if (this.#atEnd) {
      this.refs.track.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      this.#move(1);
    }
  }

  /** @param {number} steps */
  #move(steps) {
    const { track } = this.refs;
    const slide = track.querySelector(':scope > li');
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const width = slide ? slide.getBoundingClientRect().width + gap : track.clientWidth;
    track.scrollBy({ left: steps * width * this.#direction, behavior: 'smooth' });
  }

  get #direction() {
    return getComputedStyle(this.refs.track).direction === 'rtl' ? -1 : 1;
  }

  /** Scroll distance from the start, positive in both writing directions. */
  get #offset() {
    return Math.abs(this.refs.track.scrollLeft);
  }

  get #scrollable() {
    const { track } = this.refs;
    return track.scrollWidth - track.clientWidth > 2;
  }

  get #atEnd() {
    const { track } = this.refs;
    return this.#offset + track.clientWidth >= track.scrollWidth - 2;
  }

  #queueUpdate() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = undefined;
      this.#update();
    });
  }

  #update() {
    const { previousButton, nextButton, controls } = this.refs;
    if (controls) controls.hidden = !this.#scrollable;
    if (previousButton) previousButton.disabled = this.#offset <= 2;
    if (nextButton) nextButton.disabled = this.#atEnd;
    if (!this.#timer) this.#restartTimer();
  }

  #renderPauseButton() {
    const { pauseButton, pauseIcon, playIcon } = this.refs;
    if (!pauseButton) return;
    pauseButton.hidden = !this.#autoplay;
    pauseButton.setAttribute('aria-label', (this.#userPaused ? this.dataset.labelPlay : this.dataset.labelPause) ?? '');
    if (pauseIcon) pauseIcon.hidden = this.#userPaused;
    if (playIcon) playIcon.hidden = !this.#userPaused;
  }
}

if (!customElements.get('gp-carousel')) {
  customElements.define('gp-carousel', GpCarousel);
}
